import type { KlearFinancialAccount } from '@klear/klear-financial-account/klear-financial-account'
import { KLEAR_ACCOUNT_TYPE_RECEIVABLE, type KlearAccountType } from '@klear/klear-financial-account/constants'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'
import constant from 'lodash/constant'
import isFinite from 'lodash/isFinite'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'

const SELECTED_ACCOUNT_STORAGE_PREFIX = 'klear.selectedKlearAccountId.org.'
export const LEGACY_SELECTED_KLEAR_ACCOUNT_STORAGE_KEY = 'klear.selectedKlearAccountId'

let selectedAccountIdStore: number | null = null
let selectedOrgIdStore: number | null = null
const selectedAccountListeners = new Set<() => void>()

export function selectedKlearAccountStorageKey(organizationId: number): string {
  return `${SELECTED_ACCOUNT_STORAGE_PREFIX}${organizationId}`
}

function storageKeyForOrg(organizationId: number): string {
  return selectedKlearAccountStorageKey(organizationId)
}

function readStoredAccountId(organizationId: number): number | null {
  if (typeof window === 'undefined') return null
  const scoped = window.localStorage.getItem(storageKeyForOrg(organizationId))
  if (scoped) {
    const parsed = toNumber(scoped)
    return isFinite(parsed) && parsed > 0 ? parsed : null
  }
  const legacy = window.localStorage.getItem(LEGACY_SELECTED_KLEAR_ACCOUNT_STORAGE_KEY)
  if (!legacy) return null
  const parsed = toNumber(legacy)
  if (!isFinite(parsed) || parsed <= 0) {
    window.localStorage.removeItem(LEGACY_SELECTED_KLEAR_ACCOUNT_STORAGE_KEY)
    return null
  }
  window.localStorage.setItem(storageKeyForOrg(organizationId), toString(parsed))
  window.localStorage.removeItem(LEGACY_SELECTED_KLEAR_ACCOUNT_STORAGE_KEY)
  return parsed
}

function writeStoredAccountId(organizationId: number | null, id: number | null) {
  if (typeof window === 'undefined' || organizationId == null) return
  const key = storageKeyForOrg(organizationId)
  if (id == null) {
    window.localStorage.removeItem(key)
    return
  }
  window.localStorage.setItem(key, toString(id))
}

function emitSelectedAccountChange() {
  for (const listener of selectedAccountListeners) {
    listener()
  }
}

function getSelectedAccountIdSnapshot(): number | null {
  return selectedAccountIdStore
}

function subscribeSelectedAccountId(listener: () => void): () => void {
  selectedAccountListeners.add(listener)
  return () => {
    selectedAccountListeners.delete(listener)
  }
}

function setSelectedAccountIdStore(organizationId: number | null, id: number | null) {
  if (selectedAccountIdStore === id && selectedOrgIdStore === organizationId) return
  selectedAccountIdStore = id
  selectedOrgIdStore = organizationId
  writeStoredAccountId(organizationId, id)
  emitSelectedAccountChange()
}

function pickDefaultAccount(accounts: KlearFinancialAccount[]): KlearFinancialAccount | null {
  if (accounts.length === 0) return null
  const receivable = accounts.find((a) => a.accountType === KLEAR_ACCOUNT_TYPE_RECEIVABLE)
  return receivable ?? accounts[0] ?? null
}

export function klearAccountOptionLabel(account: KlearFinancialAccount): string {
  return account.isOpen ? account.nameAndLast4 : `${account.nameAndLast4} (Closed)`
}

export function useKlearFinancialAccount({ enabled = true }: { enabled?: boolean } = {}) {
  const {
    data: accountList,
    error: listError,
    isLoading: listLoading
  } = trpc.klearAccount.list.useQuery(undefined, {
    enabled,
    refetchOnWindowFocus: true,
    refetchOnMount: true
  })

  const accountsFromList = useMemo(() => accountList ?? [], [accountList])

  const organizationId = accountsFromList[0]?.organizationId ?? null

  const receivableAccount = useMemo(() => {
    return accountsFromList.find((a) => a.accountType === KLEAR_ACCOUNT_TYPE_RECEIVABLE) ?? null
  }, [accountsFromList])

  const selectedAccountId = useSyncExternalStore(
    subscribeSelectedAccountId,
    getSelectedAccountIdSnapshot,
    constant(null)
  )

  useEffect(() => {
    if (!enabled) return
    if (organizationId == null) {
      if (accountsFromList.length === 0) {
        setSelectedAccountIdStore(null, null)
      }
      return
    }
    if (selectedOrgIdStore !== organizationId) {
      const stored = readStoredAccountId(organizationId)
      const valid = stored != null && accountsFromList.some((a) => a.id === stored)
      const next = valid ? stored : (pickDefaultAccount(accountsFromList)?.id ?? null)
      setSelectedAccountIdStore(organizationId, next)
      return
    }
    if (accountsFromList.length === 0) {
      setSelectedAccountIdStore(organizationId, null)
      return
    }
    const selectedStillValid = selectedAccountId != null && accountsFromList.some((a) => a.id === selectedAccountId)
    if (selectedStillValid) return
    const defaultAccount = pickDefaultAccount(accountsFromList)
    setSelectedAccountIdStore(organizationId, defaultAccount?.id ?? null)
  }, [enabled, accountsFromList, organizationId, selectedAccountId])

  const setSelectedAccountId = useCallback(
    (id: number) => {
      setSelectedAccountIdStore(organizationId, id)
    },
    [organizationId]
  )

  const {
    data: selectedFromServer,
    error: selectedError,
    isLoading: selectedLoading,
    isFetching: selectedFetching
  } = trpc.klearAccount.getById.useQuery(
    { id: selectedAccountId! },
    {
      enabled: enabled && selectedAccountId != null && selectedAccountId > 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  )

  const accounts = useMemo(() => {
    if (!selectedFromServer?.id) return accountsFromList
    return accountsFromList.map((account) => (account.id === selectedFromServer.id ? selectedFromServer : account))
  }, [accountsFromList, selectedFromServer])

  const openAccounts = useMemo(() => accounts.filter((account) => account.isOpen), [accounts])

  const klearFinancialAccount = useMemo(() => {
    if (selectedFromServer && selectedFromServer.id === selectedAccountId) {
      return selectedFromServer
    }
    if (accounts.length > 0) {
      const selected = accounts.find((a) => a.id === selectedAccountId)
      return selected ?? pickDefaultAccount(accounts)
    }
    return receivableAccount
  }, [selectedFromServer, accounts, selectedAccountId, receivableAccount])

  const { data: signedAgreements, error: signedAgreementsError } =
    trpc.klearAccount.getSignedLegalAgreementUrl.useQuery(undefined, {
      enabled: accountsFromList.length > 0
    })

  const signedKlearDocUrlsByAccountId = useMemo(() => {
    const map = new Map<number, string>()
    for (const agreement of signedAgreements ?? []) {
      map.set(agreement.klearAccountId, agreement.url)
    }
    return map
  }, [signedAgreements])

  const signedKlearDocUrl =
    klearFinancialAccount?.id != null ? (signedKlearDocUrlsByAccountId.get(klearFinancialAccount.id) ?? null) : null

  return {
    klearFinancialAccount,
    receivableAccount,
    accounts,
    openAccounts,
    selectedAccountId: selectedAccountId ?? klearFinancialAccount?.id ?? null,
    setSelectedAccountId,
    hasMultipleAccounts: accounts.length > 1,
    error: listError ?? selectedError ?? signedAgreementsError,
    isLoading: listLoading || (selectedAccountId != null && selectedLoading && !selectedFromServer),
    isRefreshingSelected: selectedFetching,
    signedKlearDocUrl,
    signedKlearDocUrlsByAccountId
  }
}

const invalidateQueries = ['klearAccount.list', 'klearAccount.getById', 'klearAccount.getTransactions']

export function useKlearFinancialAccountMutations() {
  const createKlearFinancialAccountMutation = useMutationWithToast(
    trpc.klearAccount.create.useMutation,
    createToastConfig<KlearFinancialAccount>('Klear Financial Account', 'create'),
    {
      invalidateQueries
    }
  )

  const refreshKlearFinancialAccountMutation = useMutationWithToast(
    trpc.klearAccount.refresh.useMutation,
    {
      successTitle: 'Klear Financial Account refreshed successfully',
      successDescription: constant('Account information has been updated'),
      errorTitle: 'Error refreshing Klear Financial Account',
      errorDescription: (error) => error.message || 'Failed to refresh account information'
    },
    {
      invalidateQueries
    }
  )

  const createKlearFinancialAccount = async () => {
    return await createKlearFinancialAccountMutation.mutateAsync()
  }

  const refreshKlearFinancialAccount = async (klearAccountId?: number) => {
    return (
      (await refreshKlearFinancialAccountMutation.mutateAsync(klearAccountId != null ? { klearAccountId } : {})) ?? null
    )
  }

  return {
    createKlearFinancialAccount,
    refreshKlearFinancialAccount,
    isCreating: createKlearFinancialAccountMutation.isPending,
    isRefreshing: refreshKlearFinancialAccountMutation.isPending
  }
}

export function useAdminKlearFinancialAccounts(organizationId: number | undefined) {
  const enabled = organizationId != null && organizationId > 0
  const {
    data: accountList,
    error,
    isLoading,
    refetch
  } = trpc.klearAccount.listForOrganization.useQuery(
    { organizationId: organizationId! },
    { enabled, refetchOnWindowFocus: true }
  )

  const accounts = useMemo(() => accountList ?? [], [accountList])

  const createForOrganizationMutation = useMutationWithToast(
    trpc.klearAccount.createForOrganization.useMutation,
    createToastConfig<KlearFinancialAccount>('Klear Financial Account', 'create'),
    {
      invalidateQueries: ['klearAccount.listForOrganization', 'klearAccount.list']
    }
  )

  const createAccount = async (accountType: Exclude<KlearAccountType, 'receivable'>) => {
    if (!organizationId) throw new Error('Organization id required')
    try {
      const result = await createForOrganizationMutation.mutateAsync({ organizationId, accountType })
      await refetch()
      return result
    } catch {
      return null
    }
  }

  return {
    accounts,
    error,
    isLoading,
    createAccount,
    isCreating: createForOrganizationMutation.isPending,
    refetch
  }
}
